import { apiGetJSON } from "../client.js";
import { composeAbortSignal, isAbortError, requestsCurrent, startRequest, cancelRequest } from "../core/request-coordinator.js";

export async function loadWorkspaceContentTree(state, apiKey, workspaceID, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  const fetchJSON = deps.apiGetJSON || apiGetJSON;
  const request = startRequest(state.requests.tree);
  const signal = composeAbortSignal(request.signal, options.signal, options.request?.signal);
  const refInput = Object.prototype.hasOwnProperty.call(options, "ref")
    ? String(options.ref || "")
    : String(state.refInput || "");
  const targetPath = Object.prototype.hasOwnProperty.call(options, "path")
    ? String(options.path || "")
    : String(state.path || "");
  const preserveFile = Boolean(options.preserveFile);
  Object.assign(state, {
    workspaceID,
    refInput,
    path: targetPath,
    loadingTree: true,
    treeError: "",
    ...(preserveFile ? {} : { selectedFilePath: "", file: null, loadingFile: false, fileError: "" })
  });
  if (!preserveFile) {
    cancelRequest(state.requests.file);
  }
  if (options.render) options.render();
  try {
    const query = new URLSearchParams();
    if (refInput.trim()) query.set("ref", refInput.trim());
    if (targetPath) query.set("path", targetPath);
    const suffix = query.toString();
    const result = await fetchJSON(apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/tree${suffix ? `?${suffix}` : ""}`, { signal });
    if (!requestsCurrent(request, options.request)) return;
    Object.assign(state, {
      workspaceID,
      refInput,
      ref: String(result.ref || refInput.trim()),
      commit: String(result.commit || ""),
      path: String(result.path || ""),
      entries: Array.isArray(result.entries) ? result.entries : [],
      loadingTree: false,
      treeError: ""
    });
    if (options.render) options.render();
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(request, options.request)) return;
    Object.assign(state, {
      loadingTree: false,
      treeError: error.message || "Failed to load canonical tree."
    });
    if (options.render) options.render();
    throw error;
  }
}

export async function loadWorkspaceContentFile(state, apiKey, workspaceID, filePath, options = {}, deps = {}) {
  if (!apiKey) throw new Error("API key is required.");
  if (!workspaceID || !filePath) return;
  const fetchJSON = deps.apiGetJSON || apiGetJSON;
  const request = startRequest(state.requests.file);
  const signal = composeAbortSignal(request.signal, options.signal, options.request?.signal);
  const refInput = String(state.refInput || "");
  Object.assign(state, {
    selectedFilePath: filePath,
    loadingFile: true,
    fileError: "",
    file: null,
    fileEditing: false,
    fileSaving: false,
    fileDraftContent: null
  });
  if (options.render) options.render();
  try {
    const query = new URLSearchParams();
    if (refInput.trim()) query.set("ref", refInput.trim());
    query.set("path", filePath);
    const result = await fetchJSON(apiKey, `/v1/workspaces/${encodeURIComponent(workspaceID)}/file?${query.toString()}`, { signal });
    if (!requestsCurrent(request, options.request)) return;
    Object.assign(state, {
      ref: String(result.ref || refInput.trim()),
      commit: String(result.commit || state.commit || ""),
      selectedFilePath: String(result.path || filePath),
      file: result,
      loadingFile: false,
      fileError: ""
    });
    if (options.render) options.render();
  } catch (error) {
    if (isAbortError(error) || !requestsCurrent(request, options.request)) return;
    Object.assign(state, {
      selectedFilePath: filePath,
      loadingFile: false,
      fileError: error.message || "Failed to load file preview.",
      file: null
    });
    if (options.render) options.render();
    throw error;
  }
}
